<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from '@/stores/library'
import FolderCard from '@/components/FolderCard.vue'
import ScanCard from '@/components/ScanCard.vue'
import ScanPreviewModal from '@/components/ScanPreviewModal.vue'
import { scansApi } from '@/api/scansApi'
import type { FolderDto, ScanDto } from '@/models/library'

const route = useRoute()
const router = useRouter()
const store = useLibraryStore()
const {
  loading,
  busy,
  error,
  scans,
  subFolders,
  breadcrumb,
  atRoot,
  selectedCount,
  selectedImageIds,
  sortMode
} = storeToRefs(store)

const fileInput = ref<HTMLInputElement | null>(null)
const preview = ref<ScanDto | null>(null)
const draggedId = ref<string | null>(null)

function currentFolderIdFromRoute(): string | null {
  const id = route.params.folderId
  return typeof id === 'string' && id.length > 0 ? id : null
}

onMounted(() => {
  // Warm the document-detector download in the background (low priority) so the camera
  // opens without a long wait. The compressed asset is ~2-3 MB.
  if (!document.getElementById('opencv-prefetch')) {
    const link = document.createElement('link')
    link.id = 'opencv-prefetch'
    link.rel = 'prefetch'
    link.as = 'script'
    link.href = '/opencv/opencv.js'
    document.head.appendChild(link)
  }
})

watch(
  () => route.params.folderId,
  () => store.load(currentFolderIdFromRoute()),
  { immediate: true }
)

function openFolder(folder: FolderDto): void {
  router.push(`/library/${folder.id}`)
}

function goTo(folderId: string | null): void {
  router.push(folderId ? `/library/${folderId}` : '/library')
}

async function newFolder(): Promise<void> {
  const name = window.prompt('New folder name')
  if (name && name.trim()) {
    await store.createFolder(name.trim())
  }
}

async function renameFolder(folder: FolderDto): Promise<void> {
  const name = window.prompt('Rename folder', folder.name)
  if (name && name.trim() && name.trim() !== folder.name) {
    await store.renameFolder(folder.id, name.trim())
  }
}

async function deleteFolder(folder: FolderDto): Promise<void> {
  if (window.confirm(`Delete folder "${folder.name}" and everything inside it?`)) {
    await store.deleteFolder(folder.id)
  }
}

function triggerUpload(): void {
  fileInput.value?.click()
}

function goCapture(): void {
  router.push(`/library/${route.params.folderId}/capture`)
}

async function onDrop(targetId: string): Promise<void> {
  const from = draggedId.value
  draggedId.value = null
  // Manual drag-reorder only applies to the stored order; ignore while a date sort is active.
  if (store.sortMode !== 'Manual') return
  if (!from || from === targetId) return
  const ids = scans.value.map((s) => s.id)
  const fromIdx = ids.indexOf(from)
  const toIdx = ids.indexOf(targetId)
  if (fromIdx < 0 || toIdx < 0) return
  ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0])
  const folderId = route.params.folderId
  if (typeof folderId !== 'string') return
  await scansApi.reorder(folderId, ids)
  await store.refresh()
}

async function onFilesChosen(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const files = input.files ? Array.from(input.files) : []
  if (files.length > 0) {
    await store.uploadFiles(files)
  }
  input.value = ''
}

async function combine(): Promise<void> {
  const name = window.prompt('Name for the combined PDF', 'Combined document')
  if (name !== null) {
    await store.combineSelected(name.trim() || undefined)
  }
}

async function deleteSelected(): Promise<void> {
  if (window.confirm(`Delete ${selectedCount.value} selected scan(s)?`)) {
    await store.deleteSelected()
  }
}

async function deleteFromPreview(): Promise<void> {
  if (preview.value && window.confirm(`Delete "${preview.value.name}"?`)) {
    await store.deleteScan(preview.value.id)
    preview.value = null
  }
}
</script>

<template>
  <div class="container">
    <!-- Breadcrumb -->
    <nav class="crumbs">
      <button class="crumb" @click="goTo(null)">🏠 Home</button>
      <template v-for="crumb in breadcrumb" :key="crumb.id">
        <span class="sep">/</span>
        <button class="crumb" @click="goTo(crumb.id)">{{ crumb.name }}</button>
      </template>
    </nav>

    <!-- Toolbar -->
    <div class="toolbar">
      <button class="btn" :disabled="busy" @click="newFolder">📁 New folder</button>
      <button class="btn btn-primary" :disabled="busy || atRoot" @click="triggerUpload" :title="atRoot ? 'Open a folder first' : ''">
        ⬆️ Upload
      </button>
      <button v-if="!atRoot" class="btn" :disabled="busy" @click="goCapture">📷 Camera</button>
      <button v-if="!atRoot && scans.length > 0" class="btn" :disabled="busy" @click="store.zipCurrentFolder()">
        🗜️ Zip folder
      </button>
      <input
        ref="fileInput"
        type="file"
        class="hidden-input"
        multiple
        accept="image/jpeg,image/png,image/webp"
        @change="onFilesChosen"
      />
    </div>

    <p v-if="atRoot" class="hint muted">Create a folder, then open it to upload scans.</p>

    <div v-if="error" class="card error">
      <strong>{{ error.title }}</strong>
      <p v-if="error.detail" class="muted">{{ error.detail }}</p>
    </div>

    <p v-if="loading" class="muted">Loading…</p>

    <template v-else>
      <!-- Folders -->
      <section v-if="subFolders.length > 0" class="section">
        <h2>Folders</h2>
        <div class="folder-grid">
          <FolderCard
            v-for="folder in subFolders"
            :key="folder.id"
            :folder="folder"
            @open="openFolder(folder)"
            @rename="renameFolder(folder)"
            @delete="deleteFolder(folder)"
          />
        </div>
      </section>

      <!-- Scans -->
      <section v-if="scans.length > 0" class="section">
        <div class="section-head">
          <h2>Scans</h2>
          <div class="section-tools">
            <label class="sort">
              <span class="sort-label">Sort</span>
              <select
                :value="sortMode"
                :disabled="busy"
                @change="store.setSortMode(($event.target as HTMLSelectElement).value as any)"
              >
                <option value="Manual">Manual order</option>
                <option value="DateAsc">Capture date ↑ (oldest first)</option>
                <option value="DateDesc">Capture date ↓ (newest first)</option>
              </select>
            </label>
            <button class="link" @click="store.selectAll()">Select all</button>
          </div>
        </div>
        <p v-if="sortMode !== 'Manual'" class="sort-hint muted">
          Sorted by capture/upload date (not edit time), so cropping a scan never changes its
          place. This choice is saved for this folder. Combine and Zip follow this order.
          Switch to “Manual order” to drag scans.
        </p>
        <div class="scan-grid">
          <div
            v-for="scan in scans"
            :key="scan.id"
            class="drag-wrap"
            :draggable="sortMode === 'Manual'"
            @dragstart="draggedId = scan.id"
            @dragover.prevent
            @drop="onDrop(scan.id)"
            @dragend="draggedId = null"
          >
            <ScanCard
              :scan="scan"
              :selected="store.isSelected(scan.id)"
              @toggle="store.toggleSelect(scan.id)"
              @open="preview = scan"
            />
          </div>
        </div>
      </section>

      <div v-if="subFolders.length === 0 && scans.length === 0" class="card empty">
        <p>This folder is empty.</p>
        <p class="muted">Create a subfolder or upload scans to get started.</p>
      </div>
    </template>

    <!-- Selection action bar -->
    <div v-if="selectedCount > 0" class="action-bar">
      <span class="count">{{ selectedCount }} selected</span>
      <div class="action-buttons">
        <button class="btn" :disabled="busy" @click="store.zipSelected()">🗜️ Zip</button>
        <button
          class="btn"
          :disabled="busy || selectedImageIds.length === 0"
          :title="selectedImageIds.length === 0 ? 'Select images to combine' : ''"
          @click="combine"
        >
          📄 Combine
        </button>
        <button class="btn danger" :disabled="busy" @click="deleteSelected">🗑️ Delete</button>
        <button class="btn" @click="store.clearSelection()">Clear</button>
      </div>
    </div>

    <ScanPreviewModal
      v-if="preview"
      :scan="preview"
      @close="preview = null"
      @delete="deleteFromPreview"
      @changed="store.refresh()"
    />
  </div>
</template>

<style scoped>
.crumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.crumb {
  background: transparent;
  border: 0;
  color: var(--primary);
  font-weight: 600;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
}

.sep {
  color: var(--text-muted);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.hidden-input {
  display: none;
}

.hint {
  margin: 0 0 1rem;
}

.muted {
  color: var(--text-muted);
}

.section {
  margin-bottom: 1.5rem;
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

h2 {
  font-size: 1rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.6rem;
}

.link {
  background: transparent;
  border: 0;
  color: var(--primary);
  cursor: pointer;
  font-size: 0.85rem;
}

.section-tools {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sort {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.sort select {
  font: inherit;
  padding: 0.2rem 0.4rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
}

.sort-hint {
  margin: 0 0 0.6rem;
  font-size: 0.8rem;
}

.folder-grid {
  display: grid;
  gap: 0.6rem;
  grid-template-columns: 1fr;
}

@media (min-width: 560px) {
  .folder-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.scan-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, 1fr);
}

@media (min-width: 560px) {
  .scan-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 800px) {
  .scan-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.empty {
  text-align: center;
}

.action-bar {
  position: sticky;
  bottom: 0;
  margin-top: 1rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.6rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  box-shadow: var(--shadow);
  flex-wrap: wrap;
}

.action-bar .count {
  font-weight: 600;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.btn.danger {
  border-color: var(--danger);
  color: var(--danger);
}

.error strong {
  color: var(--danger);
}
</style>
